import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Tabs, Tab, CircularProgress, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { siteService } from 'components/api/sites';
import { WheelSettingsData, SubTier, Prize } from 'types/wheel';
import { commonGridStyles } from 'styles/gridStyles';
import { Alert } from 'components/alert/alert';
import ComponentLoader from 'components/common/ComponentLoader';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import styles from './WheelSettings.module.scss';

interface WheelSettingsProps {
  siteId: string | number;
}

const WheelSettings: React.FC<WheelSettingsProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<WheelSettingsData | null>(null);
  const [activeTier, setActiveTier] = useState<string>('');
  const [activeSubTierIdx, setActiveSubTierIdx] = useState(0);
  const [isChanged, setIsChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null,
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showFeedback = useCallback((message: string, type: 'success' | 'error') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setFeedback({ message, type });
    timeoutRef.current = setTimeout(() => {
      setFeedback(null);
      timeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    fetchSettings();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const filteredData = useMemo(() => {
    if (!data) return null;
    const result: WheelSettingsData = {};
    Object.keys(data).forEach((tier) => {
      const subTiers = data[tier].filter((st) => st.index !== 0).sort((a, b) => a.index - b.index);
      if (subTiers.length > 0) {
        result[tier] = subTiers;
      }
    });
    return result;
  }, [data]);

  const dynamicTiers = useMemo(() => {
    if (!filteredData) return [];
    return Object.keys(filteredData)
      .map((tier) => {
        const firstSubTier = filteredData[tier][0];
        return {
          tier,
          tierName: firstSubTier?.tierName || tier,
          index: firstSubTier?.index || 0,
        };
      })
      .sort((a, b) => a.index - b.index);
  }, [filteredData]);

  const activeSubTier: SubTier | null = useMemo(() => {
    if (!filteredData || !activeTier || !filteredData[activeTier]) return null;
    return filteredData[activeTier][activeSubTierIdx] || null;
  }, [filteredData, activeTier, activeSubTierIdx]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await siteService.getWheelSettings(siteId);
      setData(response.data);

      const responseData = response.data;
      const validTiers = Object.keys(responseData).filter((tier) =>
        responseData[tier].some((st) => st.index !== 0),
      );

      if (validTiers.length > 0 && !activeTier) {
        setActiveTier(validTiers[0]);
      }
      setIsChanged(false);
    } catch (error) {
      console.error('Failed to fetch wheel settings:', error);
    } finally {
      setLoading(false);
    }
  }, [siteId, activeTier]);

  const handleTierChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTier(newValue);
    setActiveSubTierIdx(0);
    setFeedback(null);
  };

  const handleSubTierChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveSubTierIdx(newValue);
    setFeedback(null);
  };

  const handleInputChange = (
    prizeId: number,
    field: 'amount' | 'probability' | 'label',
    value: string,
  ) => {
    if (!data || !activeTier || !activeSubTier) return;

    const newData = { ...data };
    const tierArray = [...newData[activeTier]];
    const originalSubTierIdx = tierArray.findIndex((st) => st.key === activeSubTier.key);

    if (originalSubTierIdx !== -1) {
      const subTier = { ...tierArray[originalSubTierIdx] };
      const wheel = { ...subTier.wheel };
      const prizes = [...wheel.prizes];

      const prizeIndex = prizes.findIndex((p) => p.id === prizeId);

      if (prizeIndex !== -1) {
        prizes[prizeIndex] = { ...prizes[prizeIndex], [field]: value };
        wheel.prizes = prizes;
        subTier.wheel = wheel;
        tierArray[originalSubTierIdx] = subTier;
        newData[activeTier] = tierArray;

        setData(newData);
        setIsChanged(true);
      }
    }
  };

  const handleSave = async () => {
    if (!data || !activeSubTier) return;

    try {
      setSaving(true);
      setFeedback(null);
      const response = await siteService.updateWheelPrizes({
        siteId: Number(siteId),
        levelKey: activeSubTier.key,
        prizes: activeSubTier.wheel.prizes.map((p: Prize) => ({
          prizeType: p.prizeType,
          amount: p.amount,
          probability: p.probability,
          label: p.label,
          sortOrder: p.sortOrder,
        })),
      });
      setIsChanged(false);
      showFeedback(response.message || 'Changes saved successfully', 'success');
      dispatch(
        showToast({
          text: response.message || 'Wheel prizes saved successfully!',
          type: 'success',
        }),
      );
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to save changes';
      showFeedback(message, 'error');
      dispatch(showToast({ text: message, type: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 70, sortable: false },
      {
        field: 'prizeType',
        headerName: 'Type',
        width: 150,
        sortable: false,
        renderCell: (params) => <span className={styles.prizeType}>{params.value}</span>,
      },
      {
        field: 'label',
        headerName: 'Label',
        width: 200,
        sortable: false,
        renderCell: (params) => (
          <input
            type="text"
            className={styles.cellInput}
            value={params.value}
            onChange={(e) => handleInputChange(params.row.id, 'label', e.target.value)}
            disabled={!activeSubTier?.wheel?.isEnabled}
          />
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount ($)',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <input
            type="text"
            className={styles.cellInput}
            value={params.value}
            onChange={(e) => handleInputChange(params.row.id, 'amount', e.target.value)}
            disabled={!activeSubTier?.wheel?.isEnabled}
          />
        ),
      },
      {
        field: 'probability',
        headerName: 'Probability',
        sortable: false,
        width: 120,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '4px' }}>
            <input
              type="text"
              className={styles.cellInput}
              value={params.value}
              onChange={(e) => handleInputChange(params.row.id, 'probability', e.target.value)}
              disabled={!activeSubTier?.wheel?.isEnabled}
            />
            <span style={{ color: 'var(--text-secondary)' }}>%</span>
          </Box>
        ),
      },
      { field: 'sortOrder', headerName: 'Sort', width: 80, sortable: false },
    ],
    [activeSubTier, activeTier, data],
  );

  if (!data || dynamicTiers.length === 0) {
    if (loading) {
      return (
        <Box className={styles.loadingWrapper}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="textSecondary">No wheel settings found for this site.</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Alert message={feedback?.message} type={feedback?.type} />

      <Box className={styles.tabsContainer}>
        <Tabs
          value={activeTier}
          onChange={handleTierChange}
          className={styles.tierTabs}
          variant="scrollable"
          scrollButtons="auto"
        >
          {dynamicTiers.map(({ tier, tierName }) => (
            <Tab
              key={tier}
              color="red"
              label={tierName ? tierName.toUpperCase() : tier.toUpperCase()}
              value={tier}
            />
          ))}
        </Tabs>

        {activeTier && filteredData && filteredData[activeTier] && (
          <Tabs
            value={activeSubTierIdx}
            onChange={handleSubTierChange}
            className={styles.subTierTabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            {filteredData[activeTier].map((subTier, index) => (
              <Tab key={subTier.key} label={subTier.name} value={index} />
            ))}
          </Tabs>
        )}
      </Box>

      {activeSubTier && (
        <>
          <Box
            className={`${styles.gridWrapper} ${!activeSubTier.wheel.isEnabled ? styles.gridInactive : ''}`}
            sx={{ position: 'relative' }}
          >
            <ComponentLoader loading={loading || saving} />
            {isChanged && (
              <Box className={styles.statusBanner}>
                <Button
                  variant="contained"
                  className={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
            <DataGrid
              rows={activeSubTier.wheel.prizes}
              columns={columns}
              sx={commonGridStyles}
              disableColumnMenu
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default WheelSettings;
